import { createContext, ReactNode, useCallback, useEffect, useReducer, useRef } from 'react';
import OneSignal from 'react-onesignal';
import axios from '../utils/axios';
import { setSession } from '../utils/jwt';
import { socket } from '../socket';

export interface AuthUser {
  id?: string;
  email?: string;
  name?: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  // Team the caller belongs to (member) or owns (builder/subbie), resolved
  // server-side via the teamMembers table — use instead of legacy invitedBy.
  team?: { ownerUserId?: string | number | null };
  [key: string]: unknown;
}

export interface OnlineUsers {
  [userId: string]: unknown;
}

interface ImpersonationState {
  isImpersonating: boolean;
  impersonationLogId: number | null;
  originalUser: AuthUser | null;
}

interface AuthState {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: AuthUser | null;
  onlineUsers: OnlineUsers;
  impersonation: ImpersonationState;
}

type AuthAction =
  | { type: 'INITIALIZE'; payload: { isAuthenticated: boolean; user: AuthUser | null } }
  | { type: 'SET_USER'; payload: { user: AuthUser } }
  | { type: 'UPDATE_TOKEN'; payload: { isAuthenticated: boolean; user: AuthUser | null } }
  | { type: 'LOGIN'; payload: { user: AuthUser } }
  | { type: 'LOGOUT' }
  | { type: 'REGISTER'; payload: { user: AuthUser } }
  | { type: 'ONLINE_USERS'; payload: { users: OnlineUsers } }
  | {
      type: 'START_IMPERSONATION';
      payload: { user: AuthUser; originalUser: AuthUser; logId: number };
    }
  | { type: 'STOP_IMPERSONATION'; payload: { user: AuthUser } };

interface AuthSession {
  accessToken?: string;
  token?: string;
  refreshToken?: string;
  user: AuthUser;
}

interface AuthContextValue extends AuthState {
  method: 'jwt';
  Login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: Record<string, unknown>) => Promise<void>;
  updateLogin: (data: AuthSession) => Promise<void>;
  updateUserToken: (data: AuthSession) => Promise<void>;
  setUser: (user: AuthUser) => Promise<void>;
  updateOnlineUsers: (users: OnlineUsers) => Promise<void>;
  startImpersonation: (targetUserId: number | string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
  resetPassword: () => void;
  updateProfile: () => void;
}

// Last-known user, persisted so the app can boot straight into the dashboard
// while /v1/users/me revalidates in the background (and so a flaky network on
// app open never bounces a valid session to the login screen).
const AUTH_USER_KEY = 'authUser';

const readCachedUser = (): AuthUser | null => {
  try {
    const raw = window.localStorage.getItem(AUTH_USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
};

const writeCachedUser = (user: AuthUser | null): void => {
  try {
    if (user) window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    else window.localStorage.removeItem(AUTH_USER_KEY);
  } catch {
    /* storage full/unavailable — cache is best-effort */
  }
};

// Several endpoints write the one auth user, and their payloads are not equal:
// /users/me carries the full context (the industry's workplace vocabulary,
// team link, campaigns gate) while login / role-update / token payloads may
// carry less. A same-person update must never DOWNGRADE the user — losing
// `workplace` mid-session is what flicked the interface back to "Builder" /
// "Job Sites" the moment onboarding finished, until a reload refetched
// /users/me. Context fields the incoming payload does not know about are kept
// from the user we already have.
//
// Strictly identity-guarded: a different id (login as someone else,
// impersonation) replaces outright — inheriting another person's industry
// context would be a worse bug than the flicker this fixes.
const CONTEXT_FIELDS = ['workplace', 'team', 'campaignsEnabled'] as const;
const mergeSameUser = (prev: AuthUser | null, next: AuthUser): AuthUser => {
  if (!prev || prev.id !== next.id) return next;
  const merged = { ...next } as Record<string, unknown>;
  const previous = prev as Record<string, unknown>;
  for (const field of CONTEXT_FIELDS) {
    if (merged[field] === undefined && previous[field] !== undefined) {
      merged[field] = previous[field];
    }
  }
  return merged as AuthUser;
};

const initialImpersonation: ImpersonationState = {
  isImpersonating: false,
  impersonationLogId: null,
  originalUser: null,
};

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  onlineUsers: {},
  impersonation: initialImpersonation,
};

const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case 'SET_USER':
      return { ...state, user: { ...action.payload.user } };
    case 'UPDATE_TOKEN':
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case 'LOGIN':
      return { ...state, isAuthenticated: true, user: action.payload.user };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        impersonation: initialImpersonation,
      };
    case 'REGISTER':
      return { ...state, isAuthenticated: true, user: action.payload.user };
    case 'ONLINE_USERS':
      return { ...state, onlineUsers: { ...action.payload.users } };
    case 'START_IMPERSONATION':
      return {
        ...state,
        user: action.payload.user,
        impersonation: {
          isImpersonating: true,
          impersonationLogId: action.payload.logId,
          originalUser: action.payload.originalUser,
        },
      };
    case 'STOP_IMPERSONATION':
      return {
        ...state,
        user: action.payload.user,
        impersonation: initialImpersonation,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  method: 'jwt',
  Login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  updateLogin: () => Promise.resolve(),
  updateUserToken: () => Promise.resolve(),
  setUser: () => Promise.resolve(),
  updateOnlineUsers: () => Promise.resolve(),
  startImpersonation: () => Promise.resolve(),
  exitImpersonation: () => Promise.resolve(),
  resetPassword: () => {},
  updateProfile: () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Mirror of state.user for the absorb path below — the provider's async
  // callbacks close over a stale `state`, and merging against a stale user
  // would resurrect fields the server has since removed.
  const userRef = useRef<AuthUser | null>(null);
  userRef.current = state.user;

  // The single write path for a same-session user update: merge against the
  // user we already have (see mergeSameUser), persist the merged result, and
  // return it for dispatch. Cache and state always hold the SAME object — a
  // cache written from the raw payload while state held the merge would blink
  // construction wording once on every cold boot.
  const absorbUser = (next: AuthUser): AuthUser => {
    const merged = mergeSameUser(userRef.current, next);
    writeCachedUser(merged);
    return merged;
  };

  useEffect(() => {
    const initialize = async () => {
      const accessToken = window.localStorage.getItem('accessToken');
      if (!accessToken) {
        dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null } });
        return;
      }
      setSession(accessToken);

      // Optimistic boot: with a token + a cached user we consider the session
      // live immediately and revalidate in the background. The session only
      // ends on a DEFINITIVE server rejection (400/401 — the axios interceptor
      // has already tried the refresh token by then). A network blip, timeout
      // or 5xx on app open must NOT log the user out — that was bouncing
      // phones (which resume the app before connectivity is ready) to the
      // login screen on every open.
      const cachedUser = readCachedUser();
      if (cachedUser) {
        dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: true, user: cachedUser } });
        if (cachedUser.id) OneSignal.login(String(cachedUser.id)).catch(() => {});
      }

      try {
        const { data } = await axios.get('/v1/users/me');
        const user = data?.user || data?.data || data;
        writeCachedUser(user);
        if (cachedUser) {
          dispatch({ type: 'SET_USER', payload: { user } });
        } else {
          dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: true, user } });
          if (user?.id) OneSignal.login(String(user.id)).catch(() => {});
        }
      } catch (err) {
        const status = (err as { status?: number })?.status;
        if (status === 400 || status === 401) {
          // Refresh token rejected/expired server-side — genuine end of session.
          writeCachedUser(null);
          setSession(null);
          dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null } });
        } else if (!cachedUser) {
          // Transient failure and nothing cached to render with: keep the
          // tokens (next open will succeed) but we can't enter the app yet.
          dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: false, user: null } });
        }
        // Transient failure with a cached user: stay logged in; React Query
        // and the axios interceptor will recover as requests retry.
      }
    };
    initialize();
  }, []);

  const Login = async (email: string, password: string) => {
    setSession(null);
    const response = await axios.post('/v1/auth/login', { email, password });
    const { accessToken, refreshToken, user } = response.data;
    setSession(accessToken, refreshToken);
    const merged = absorbUser(user);
    dispatch({ type: 'LOGIN', payload: { user: merged } });
    if (merged?.id) OneSignal.login(String(merged.id)).catch(() => {});
  };

  const register = async (userData: Record<string, unknown>) => {
    setSession(null);
    const response = await axios.post('/v1/auth/register', userData);
    const { accessToken, refreshToken, user } = response.data;
    setSession(accessToken, refreshToken);
    const merged = absorbUser(user);
    dispatch({ type: 'LOGIN', payload: { user: merged } });
    if (merged?.id) OneSignal.login(String(merged.id)).catch(() => {});
  };

  const updateLogin = async (data: AuthSession) => {
    const accessToken = data.accessToken || data.token;
    setSession(accessToken, data.refreshToken);
    dispatch({ type: 'LOGIN', payload: { user: absorbUser(data.user) } });
  };

  const updateUserToken = async (data: AuthSession) => {
    const accessToken = data.accessToken || data.token;
    setSession(accessToken, data.refreshToken);
    dispatch({ type: 'SET_USER', payload: { user: absorbUser(data.user) } });
  };

  const setUser = async (user: AuthUser) => {
    dispatch({ type: 'SET_USER', payload: { user: absorbUser(user) } });
  };

  const logout = async () => {
    OneSignal.logout().catch(() => {});
    await axios.post('/v1/auth/logout').catch(() => {});
    localStorage.removeItem('impersonationLogId');
    localStorage.removeItem('adminAccessToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    writeCachedUser(null);
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const startImpersonation = useCallback(
    async (targetUserId: number | string) => {
      const currentUser = state.user;
      if (!currentUser) return;

      const response = await axios.post(`/v1/auth/impersonate/${targetUserId}`);
      const { accessToken, logId, user: targetUser } = response.data;

      // Stash admin tokens
      const adminAccessToken = localStorage.getItem('accessToken');
      const adminRefreshToken = localStorage.getItem('refreshToken');
      if (adminAccessToken) localStorage.setItem('adminAccessToken', adminAccessToken);
      if (adminRefreshToken) localStorage.setItem('adminRefreshToken', adminRefreshToken);
      localStorage.setItem('adminUser', JSON.stringify(currentUser));
      localStorage.setItem('impersonationLogId', String(logId));

      // Set impersonation token (no refresh token)
      localStorage.setItem('accessToken', accessToken);
      localStorage.removeItem('refreshToken');
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      writeCachedUser(targetUser);

      // Disconnect socket during impersonation
      socket.disconnect();

      dispatch({
        type: 'START_IMPERSONATION',
        payload: { user: targetUser, originalUser: currentUser, logId },
      });
    },
    [state.user]
  );

  const exitImpersonation = useCallback(async () => {
    const logId = localStorage.getItem('impersonationLogId');
    const adminAccessToken = localStorage.getItem('adminAccessToken');
    const adminRefreshToken = localStorage.getItem('adminRefreshToken');
    const adminUserJson = localStorage.getItem('adminUser');

    // Restore admin session first so the /end call uses admin auth
    if (adminAccessToken) {
      localStorage.setItem('accessToken', adminAccessToken);
      axios.defaults.headers.common.Authorization = `Bearer ${adminAccessToken}`;
    }
    if (adminRefreshToken) {
      localStorage.setItem('refreshToken', adminRefreshToken);
    }

    // Log the end of impersonation
    if (logId) {
      await axios
        .post('/v1/auth/impersonate/end', { logId: Number(logId) })
        .catch(() => {});
    }

    // Clean up impersonation keys
    localStorage.removeItem('impersonationLogId');
    localStorage.removeItem('adminAccessToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');

    // Reconnect socket as admin
    socket.connect();

    const adminUser = adminUserJson ? JSON.parse(adminUserJson) : null;
    if (adminUser) {
      writeCachedUser(adminUser);
      dispatch({ type: 'STOP_IMPERSONATION', payload: { user: adminUser } });
    }
  }, []);

  const updateOnlineUsers = async (users: OnlineUsers) => {
    dispatch({ type: 'ONLINE_USERS', payload: { users } });
  };

  const resetPassword = () => {};
  const updateProfile = () => {};

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        Login,
        logout,
        register,
        updateLogin,
        resetPassword,
        updateProfile,
        updateUserToken,
        setUser,
        updateOnlineUsers,
        startImpersonation,
        exitImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
